import type { ComponentProps } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

/**
 * Renders trusted markdown produced by the platform's LLM agents
 * (Analyst `custom_proposal_narrative`, per-tool `rationale`) using a
 * hardened `react-markdown` posture:
 *
 * - `skipHtml` — any inline `<script>` / `<img>` / `<iframe>` markup a
 *   downstream prompt might emit is dropped rather than parsed.
 * - `disallowedElements` — belt-and-braces block on the elements the
 *   commonmark parser can still surface (raw HTML would already be
 *   stripped, but `<a>` with `javascript:` URLs is a separate risk).
 *   We disallow `script`, `iframe`, `object`, `embed`, `style`,
 *   `link`, `meta`, and `form` — the exact set enumerated in the
 *   [XSS-safe-rendering ADR](../../../../docs/decisions/2026-06-15-admin-ui-xss-safe-rendering.md).
 * - No rehype plugin allowing raw HTML — the ADR explicitly forbids
 *   `rehype-raw` and any transformer that reintroduces the raw-HTML
 *   arm.
 * - `remark-gfm` adds GitHub-flavoured lists / tables / strikethrough
 *   without touching the HTML arm.
 *
 * The `urlTransform` inherits react-markdown's default (rejects
 * `javascript:` URLs on `<a>` / `<img>`); we do not override it.
 *
 * Do NOT use this to render attacker-influenced strings from the
 * public chatbot (`problem_memos.*`, `client_sessions.conversation_history`).
 * Those fields go through `<MemoField>`'s plain text-interpolation
 * per the ADR. Callers include a runtime test asserting no injected
 * `<script>` / `<img onerror>` / `<iframe>` materialises, mirroring
 * `EntryDetail.test.tsx`.
 */
export interface SafeMarkdownProps {
  children: string;
  /** Optional class on the wrapping div so callers can apply prose spacing. */
  className?: string;
  /**
   * Component overrides forwarded to `react-markdown` — used to attach
   * Tailwind classes to headings, lists, and paragraphs so the LLM's
   * markdown matches the surrounding surface's typography.
   */
  components?: ComponentProps<typeof Markdown>["components"];
}

const DISALLOWED_ELEMENTS: readonly string[] = [
  "script",
  "iframe",
  "object",
  "embed",
  "style",
  "link",
  "meta",
  "form",
];

export function SafeMarkdown({
  children,
  className,
  components,
}: SafeMarkdownProps) {
  return (
    <div className={className}>
      <Markdown
        skipHtml
        disallowedElements={DISALLOWED_ELEMENTS}
        remarkPlugins={[remarkGfm]}
        components={components}
      >
        {children}
      </Markdown>
    </div>
  );
}
